import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Autocomplete, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PortalUserConnection } from '@/common';
import { UserSettings } from '../types';
import { updateDefaultConnection } from '../api/updateDefaultConnection';
import { getMessageFromError, useAuthStore } from '@/core';

type DefaultUserConnectionFormProps = {
  userSettings: UserSettings;
};

const DefaultUserConnectionForm = ({ userSettings }: DefaultUserConnectionFormProps) => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [defaultConnection, setDefaultConnection] = useState<PortalUserConnection | null>(
    userSettings.defaultConnection
  );
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: updateDefaultConnection,
    onSuccess: () => {
      authStore.unauthenticateUser();
      localStorage.removeItem('accessToken');
      queryClient.removeQueries();
    },
    onError: (mutationError: Error) => {
      setError(getMessageFromError(mutationError));
    },
  });

  const handleClick = () => {
    setError('');

    if (!defaultConnection) {
      setError(
        t('Default connection is required.', {
          defaultValue: 'Default connection is required.',
        }).toString()
      );
      return;
    }

    if (defaultConnection.id === userSettings.defaultConnection.id) {
      setError(
        t('This connection is already set.', {
          defaultValue: 'This connection is already set.',
        }).toString()
      );
      return;
    }

    mutation.mutate({ defaultConnectionId: defaultConnection.id });
  };

  return (
    <>
      <Autocomplete
        value={defaultConnection}
        onChange={(_, newValue: PortalUserConnection | null) => {
          setDefaultConnection(newValue);

          if (!newValue) {
            setError(
              t('Default connection is required.', {
                defaultValue: 'Default connection is required.',
              }).toString()
            );
          } else {
            setError('');
          }
        }}
        options={userSettings.connections}
        fullWidth
        getOptionLabel={(e) =>
          `${e.showAs} - ${t(e.type, {
            defaultValue: e.type,
          })}`
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            InputLabelProps={{}} // Added to fix typeerror
            label={t('Default connection', { defaultValue: 'Default connection' })}
            error={!!error}
            helperText={error}
          />
        )}
      />
      <Typography variant="caption" sx={{ ml: '0.875rem', fontWeight: '500' }}>
        {t('Note: After changing default connection you will have to log in again.', {
          defaultValue: 'Note: After changing default connection you will have to log in again.',
        })}
      </Typography>
      <Button variant="contained" fullWidth sx={{ mt: '0.8rem' }} onClick={handleClick}>
        {t('Submit', { defaultValue: 'Submit' })}
      </Button>
    </>
  );
};

export default DefaultUserConnectionForm;
